/**
 * Standard soccar arena: procedural collision mesh plus the `ArenaGeometry`
 * implementation that queries it.
 *
 * WHY PROCEDURAL. There is no canonical `pitch.obj` in the public sources (see
 * docs/CONSTANTS_SOURCES.md §U11), so the mesh is generated from the measured
 * planes and fillet radii in ./arena-data. Everything dimensional in this file
 * comes from ./constants or ./arena-data; the only free parameters are
 * tessellation densities, which are marked as such.
 *
 * SHAPE. The shell is a loft. A closed horizontal outline (the "ring") carries,
 * per point, a nominal wall position, an inward normal and the radius of the
 * floor fillet that applies there — 256 on the side walls and the 45-degree
 * corner planes, 160 on the back walls, blended across the vertical corner
 * fillets that join them. The ring is then swept up through a list of z levels,
 * with each point pushed inward by the floor-fillet profile near z = 0 and by
 * the uniform 512 wall-to-ceiling fillet near z = 2048. Floor and ceiling are
 * capped by radially subdivided fans. The two goal mouths are cut out of the
 * back walls and replaced by an extruded rounded-rectangle tube.
 *
 * KNOWN APPROXIMATIONS, all inherited from the sources rather than invented:
 *
 *  - The vertical corner fillet is documented as measurably NON-circular (local
 *    radius of curvature runs 907 -> 865; arena-data calls 862 an effective
 *    radius). We tessellate it as a true circular arc of radius
 *    `WALL_FILLETS.cornerVerticalApprox`, placed tangent to both flats. The
 *    residual against the two measured arc centres is <= 3.4 uu, and the
 *    resulting 45-degree flat run comes out 915.07 uu against the measured
 *    `CORNER_FLAT_RUN.length` of 905.76 — 9.3 uu (1.0 %) long. Those two cannot
 *    both be satisfied by any circle: exact tangency plus the measured run
 *    length implies r = 873.2, outside the sourced 862 +/- 3.
 *  - The floor fillets are documented as "not circular" by the RLBot wiki. The
 *    side-wall fit residual is 0.047 uu so the quarter circle is safe there; the
 *    back-wall 160 and the 512 ceiling seam are mesh-measurement-only.
 *  - Where the back wall meets a goal mouth its floor fillet has to vanish (the
 *    floor runs straight into the goal). No source describes that transition, so
 *    the radius is ramped 160 -> 0 over `GOAL_FILLET_BLEND` uu of x. That is a
 *    tessellation choice, not a measurement.
 *  - Ramping the floor fillet 256 -> 160 across the vertical corner fillet makes
 *    the floor edge bulge up to ~17 uu past the corner plane's own floor
 *    intercept of 7701.96, peaking near the middle of that arc. It stays well
 *    inside |x| + |y| = 8064, so nothing leaves the playable volume. The real
 *    three-way blend there is unmeasured, so there is nothing better to match.
 */

import { GOAL, WALL_FILLETS } from './arena-data';
import { ARENA, BALL_RADIUS, BALL_REST_Z } from './constants';
import { vec3, type Vec3 } from './math';
import { BVH } from './bvh';
import type { OBB } from './obb';
import {
  closestPoint,
  closestPointOnTriangleFlat,
  obbTriangleContactFlat,
  obbTriangleHit,
  rayTriangleFlat,
  type ClosestPoint,
  type OBBTriangleHit,
} from './triangle';
import type { ArenaGeometry, Contact, RayHit } from './surface';

// ---------------------------------------------------------------------------
// Dimensions (all sourced) and tessellation densities (all free)
// ---------------------------------------------------------------------------

const X_WALL = ARENA.halfWidth; // 4096
const Y_WALL = ARENA.halfLength; // 5120
const Z_CEIL = ARENA.ceiling; // 2048
const CORNER_C = ARENA.cornerPlaneIntercept; // 8064

const R_SIDE = WALL_FILLETS.sideWallToFloor; // 256
const R_BACK = WALL_FILLETS.backWallToFloor; // 160
const R_CORNER = WALL_FILLETS.cornerToFloor; // 256
const R_CEIL = WALL_FILLETS.wallToCeiling; // 512
const R_VERT = WALL_FILLETS.cornerVerticalApprox; // 862

const GOAL_HALF_WIDTH = GOAL.centerToPostMesh; // 896
const GOAL_HEIGHT = GOAL.heightMesh; // 640
const GOAL_FILLET = GOAL.cornerFilletRadius; // 96
const GOAL_BACK_Y = GOAL.backNetY; // 6000

/**
 * The ball rests at BALL_REST_Z = 93.15 with a geometric radius of 91.25. The
 * 1.9 uu difference is Bullet's collision margin on the static arena shape, not
 * part of the sphere (see the BALL_RADIUS / BALL_REST_Z notes in ./constants).
 * Reproducing the documented rest height therefore means inflating the query
 * radius here rather than inflating the ball anywhere else. Derived from two
 * confirmed constants; nothing new is being asserted.
 */
export const ARENA_COLLISION_MARGIN = BALL_REST_Z - BALL_RADIUS;

/** Free tessellation parameters. None of these change any dimension. */
const RING_MAX_SEGMENT = 600; // uu, longest horizontal chord on a straight run
const VERT_FILLET_STEPS = 8; // per 45-degree vertical corner fillet
const FLOOR_ARC_STEPS = 6; // per quarter of a floor fillet
const CEIL_ARC_STEPS = 6; // per quarter of the wall-to-ceiling fillet
const WALL_LEVELS = 4; // z bands between the fillets
const FLOOR_RADIAL_STEPS = 8; // rings in the floor cap fan
const CEIL_RADIAL_STEPS = 6; // rings in the ceiling cap fan
const GOAL_ARC_STEPS = 4; // per quarter of a goal-mouth corner fillet
const GOAL_DEPTH_BANDS = 2; // along the goal tube
const GOAL_MAX_SEGMENT = 400; // uu, on the straight parts of the goal profile
/** Ramp length for the vanishing back-wall fillet at a goal mouth. */
const GOAL_FILLET_BLEND = R_BACK;

const SQRT2 = Math.SQRT2;
const INV_SQRT2 = Math.SQRT1_2;

// ---------------------------------------------------------------------------
// Mesh builder
// ---------------------------------------------------------------------------

/**
 * Triangles below this doubled-area threshold are dropped. The loft produces
 * genuinely degenerate quads wherever a fillet radius collapses to zero (the
 * whole floor-fillet stack over a goal mouth), and a zero-area triangle has no
 * usable normal.
 */
const MIN_AREA_VECTOR_SQ = 1e-6;

class MeshBuilder {
  private readonly v: number[] = [];

  tri(a: Vec3, b: Vec3, c: Vec3): void {
    const e1x = b.x - a.x, e1y = b.y - a.y, e1z = b.z - a.z;
    const e3x = a.x - c.x, e3y = a.y - c.y, e3z = a.z - c.z;
    const nx = e3y * e1z - e3z * e1y;
    const ny = e3z * e1x - e3x * e1z;
    const nz = e3x * e1y - e3y * e1x;
    if (nx * nx + ny * ny + nz * nz < MIN_AREA_VECTOR_SQ) return;
    this.v.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  }

  /** Quad a-b-c-d, split into (a, b, c) and (a, c, d). */
  quad(a: Vec3, b: Vec3, c: Vec3, d: Vec3): void {
    this.tri(a, b, c);
    this.tri(a, c, d);
  }

  build(): Float64Array {
    return new Float64Array(this.v);
  }
}

// ---------------------------------------------------------------------------
// The horizontal outline
// ---------------------------------------------------------------------------

interface RingPoint {
  /** Nominal wall position with no fillet inset applied. */
  x: number;
  y: number;
  /** Unit inward normal (points toward the middle of the field). */
  nx: number;
  ny: number;
  /** Floor fillet radius here; 0 across a goal mouth. */
  r: number;
  /** True on the span of a back wall that a goal mouth is cut out of. */
  goal: boolean;
}

/**
 * Tangency of the vertical corner fillet, in the (+x, +y) quadrant.
 *
 * Fillet A joins the side wall x = +X_WALL to the corner plane x + y = 8064;
 * fillet B joins that corner plane to the back wall y = +Y_WALL. Both centres
 * are placed at exactly R_VERT from the two planes they blend, which is what
 * makes the arcs tangent. Measured centres are (3232.56, 3613.32) and
 * (2588.46, 4254.67); ours land 2.4 and 3.4 uu away.
 */
const FILLET_A_X = X_WALL - R_VERT;
const FILLET_A_Y = CORNER_C - R_VERT * SQRT2 - FILLET_A_X;
const FILLET_B_Y = Y_WALL - R_VERT;
const FILLET_B_X = CORNER_C - R_VERT * SQRT2 - FILLET_B_Y;

const pushLine = (
  out: RingPoint[],
  x0: number, y0: number, x1: number, y1: number,
  nx: number, ny: number,
  r0: number, r1: number,
  goal: boolean,
  maxSegment: number
): void => {
  const len = Math.hypot(x1 - x0, y1 - y0);
  const steps = Math.max(1, Math.ceil(len / maxSegment));
  // The endpoint is left to the next run so that shared vertices are emitted
  // exactly once and the ring has no duplicates.
  for (let j = 0; j < steps; j++) {
    const t = j / steps;
    out.push({
      x: x0 + (x1 - x0) * t,
      y: y0 + (y1 - y0) * t,
      nx, ny,
      r: r0 + (r1 - r0) * t,
      goal,
    });
  }
};

const pushArc = (
  out: RingPoint[],
  cx: number, cy: number, radius: number,
  a0: number, a1: number, steps: number,
  r0: number, r1: number
): void => {
  for (let j = 0; j < steps; j++) {
    const t = j / steps;
    const a = a0 + (a1 - a0) * t;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    out.push({
      x: cx + radius * ca,
      y: cy + radius * sa,
      nx: -ca,
      ny: -sa,
      r: r0 + (r1 - r0) * t,
      goal: false,
    });
  }
};

/**
 * The outline from the middle of the +x side wall, counter-clockwise seen from
 * above, to the middle of the +y back wall. The full ring is four mirrored
 * copies of this.
 */
const quarterRing = (): RingPoint[] => {
  const q: RingPoint[] = [];

  // +x side wall, up to the vertical fillet.
  pushLine(q, X_WALL, 0, X_WALL, FILLET_A_Y, -1, 0, R_SIDE, R_SIDE, false, RING_MAX_SEGMENT);
  // Vertical fillet: side wall -> 45-degree corner plane.
  pushArc(q, FILLET_A_X, FILLET_A_Y, R_VERT, 0, Math.PI / 4, VERT_FILLET_STEPS, R_SIDE, R_CORNER);
  // The flat 45-degree run, between the two fillet tangents.
  const ax = FILLET_A_X + R_VERT * INV_SQRT2;
  const ay = FILLET_A_Y + R_VERT * INV_SQRT2;
  const bx = FILLET_B_X + R_VERT * INV_SQRT2;
  const by = FILLET_B_Y + R_VERT * INV_SQRT2;
  pushLine(q, ax, ay, bx, by, -INV_SQRT2, -INV_SQRT2, R_CORNER, R_CORNER, false, RING_MAX_SEGMENT);
  // Vertical fillet: corner plane -> back wall.
  pushArc(q, FILLET_B_X, FILLET_B_Y, R_VERT, Math.PI / 4, Math.PI / 2, VERT_FILLET_STEPS, R_CORNER, R_BACK);
  // Back wall, in three runs: full fillet, the ramp to zero, then the goal span.
  const blendX = GOAL_HALF_WIDTH + GOAL_FILLET_BLEND;
  pushLine(q, FILLET_B_X, Y_WALL, blendX, Y_WALL, 0, -1, R_BACK, R_BACK, false, RING_MAX_SEGMENT);
  pushLine(q, blendX, Y_WALL, GOAL_HALF_WIDTH, Y_WALL, 0, -1, R_BACK, 0, false, GOAL_FILLET_BLEND / 2);
  pushLine(q, GOAL_HALF_WIDTH, Y_WALL, 0, Y_WALL, 0, -1, 0, 0, true, RING_MAX_SEGMENT);
  q.push({ x: 0, y: Y_WALL, nx: 0, ny: -1, r: 0, goal: true });

  return q;
};

const mirrorRing = (q: readonly RingPoint[], sx: number, sy: number): RingPoint[] =>
  q.map((p) => ({ x: p.x * sx, y: p.y * sy, nx: p.nx * sx, ny: p.ny * sy, r: p.r, goal: p.goal }));

/** The closed outline, counter-clockwise seen from above, with no duplicates. */
const buildRing = (): RingPoint[] => {
  const q = quarterRing();
  return [
    ...q,
    ...mirrorRing(q, -1, 1).reverse().slice(1),
    ...mirrorRing(q, -1, -1).slice(1),
    ...mirrorRing(q, 1, -1).reverse().slice(1, -1),
  ];
};

// ---------------------------------------------------------------------------
// The vertical profile
// ---------------------------------------------------------------------------

/** Inward offset of the floor fillet at height z, for a fillet of radius r. */
const floorInset = (z: number, r: number): number => {
  if (r <= 0 || z >= r) return 0;
  const d = r - z;
  return r - Math.sqrt(Math.max(0, r * r - d * d));
};

/** Inward offset of the uniform wall-to-ceiling fillet at height z. */
const ceilingInset = (z: number): number => {
  const z0 = Z_CEIL - R_CEIL;
  if (z <= z0) return 0;
  const d = z - z0;
  return R_CEIL - Math.sqrt(Math.max(0, R_CEIL * R_CEIL - d * d));
};

const ringPos = (p: RingPoint, z: number): Vec3 => {
  const inset = floorInset(z, p.r) + ceilingInset(z);
  return vec3(p.x + p.nx * inset, p.y + p.ny * inset, z);
};

/**
 * The shared z levels. Sampling each fillet at uniform ANGLE and then taking
 * the union of the resulting heights means every ring point still lands exactly
 * on its own arc, whichever level it is evaluated at — the two floor fillets
 * have different radii and would otherwise need different level lists.
 */
const buildLevels = (): number[] => {
  const levels: number[] = [];
  for (const r of [R_SIDE, R_BACK, R_CORNER]) {
    for (let j = 0; j <= FLOOR_ARC_STEPS; j++) {
      levels.push(r * (1 - Math.cos((j / FLOOR_ARC_STEPS) * (Math.PI / 2))));
    }
  }
  // Exact, so that the goal-mouth cut lines up with a band boundary.
  levels.push(GOAL_HEIGHT);
  const zTop = Z_CEIL - R_CEIL;
  const zMid = Math.max(R_SIDE, R_BACK, R_CORNER);
  for (let j = 0; j <= WALL_LEVELS; j++) levels.push(zMid + ((zTop - zMid) * j) / WALL_LEVELS);
  for (let j = 0; j <= CEIL_ARC_STEPS; j++) {
    levels.push(zTop + R_CEIL * Math.sin((j / CEIL_ARC_STEPS) * (Math.PI / 2)));
  }

  levels.sort((a, b) => a - b);
  const out: number[] = [];
  for (const z of levels) {
    if (out.length === 0 || z - out[out.length - 1] > 1e-6) out.push(z);
  }
  return out;
};

// ---------------------------------------------------------------------------
// Goal geometry
// ---------------------------------------------------------------------------

interface Profile2D {
  x: number;
  z: number;
}

const pushProfileLine = (out: Profile2D[], x0: number, z0: number, x1: number, z1: number): void => {
  const len = Math.hypot(x1 - x0, z1 - z0);
  const steps = Math.max(1, Math.ceil(len / GOAL_MAX_SEGMENT));
  for (let j = 0; j < steps; j++) {
    const t = j / steps;
    out.push({ x: x0 + (x1 - x0) * t, z: z0 + (z1 - z0) * t });
  }
};

const pushProfileArc = (
  out: Profile2D[],
  cx: number, cz: number, a0: number, a1: number
): void => {
  for (let j = 0; j < GOAL_ARC_STEPS; j++) {
    const a = a0 + ((a1 - a0) * j) / GOAL_ARC_STEPS;
    out.push({ x: cx + GOAL_FILLET * Math.cos(a), z: cz + GOAL_FILLET * Math.sin(a) });
  }
};

const HALF_PI = Math.PI / 2;

/** Inner corner centres of the goal cross-section: (+/-800, 96) and (+/-800, 544). */
const GOAL_CORNER_X = GOAL_HALF_WIDTH - GOAL_FILLET;
const GOAL_CORNER_Z_LO = GOAL_FILLET;
const GOAL_CORNER_Z_HI = GOAL_HEIGHT - GOAL_FILLET;

/**
 * The goal cross-section: a rounded rectangle, traversed counter-clockwise in
 * the (x, z) plane starting at the bottom-left tangent point. That direction is
 * what makes the extruded tube's normals point into the goal interior.
 */
const goalProfile = (): Profile2D[] => {
  const p: Profile2D[] = [];
  pushProfileLine(p, -GOAL_CORNER_X, 0, GOAL_CORNER_X, 0);
  pushProfileArc(p, GOAL_CORNER_X, GOAL_CORNER_Z_LO, -HALF_PI, 0);
  pushProfileLine(p, GOAL_HALF_WIDTH, GOAL_CORNER_Z_LO, GOAL_HALF_WIDTH, GOAL_CORNER_Z_HI);
  pushProfileArc(p, GOAL_CORNER_X, GOAL_CORNER_Z_HI, 0, HALF_PI);
  pushProfileLine(p, GOAL_CORNER_X, GOAL_HEIGHT, -GOAL_CORNER_X, GOAL_HEIGHT);
  pushProfileArc(p, -GOAL_CORNER_X, GOAL_CORNER_Z_HI, HALF_PI, Math.PI);
  pushProfileLine(p, -GOAL_HALF_WIDTH, GOAL_CORNER_Z_HI, -GOAL_HALF_WIDTH, GOAL_CORNER_Z_LO);
  pushProfileArc(p, -GOAL_CORNER_X, GOAL_CORNER_Z_LO, Math.PI, Math.PI + HALF_PI);
  return p;
};

/**
 * One goal: the tube from the mouth plane back to the net, its back cap, and
 * the four slivers of back wall left between the rounded mouth corners and the
 * rectangular hole cut out of the shell.
 *
 * `sy` is +1 for the orange goal at +Y. The -Y goal is the exact mirror, with
 * the winding of every triangle reversed so the normals stay inward.
 */
const buildGoal = (mesh: MeshBuilder, sy: number): void => {
  const emit = (a: Vec3, b: Vec3, c: Vec3): void => {
    if (sy > 0) mesh.tri(a, b, c);
    else mesh.tri(vec3(a.x, -a.y, a.z), vec3(c.x, -c.y, c.z), vec3(b.x, -b.y, b.z));
  };
  const emitQuad = (a: Vec3, b: Vec3, c: Vec3, d: Vec3): void => {
    emit(a, b, c);
    emit(a, c, d);
  };

  const profile = goalProfile();
  const n = profile.length;

  for (let k = 0; k < GOAL_DEPTH_BANDS; k++) {
    const y0 = Y_WALL + ((GOAL_BACK_Y - Y_WALL) * k) / GOAL_DEPTH_BANDS;
    const y1 = Y_WALL + ((GOAL_BACK_Y - Y_WALL) * (k + 1)) / GOAL_DEPTH_BANDS;
    for (let i = 0; i < n; i++) {
      const p = profile[i];
      const q = profile[(i + 1) % n];
      emitQuad(
        vec3(p.x, y0, p.z),
        vec3(q.x, y0, q.z),
        vec3(q.x, y1, q.z),
        vec3(p.x, y1, p.z)
      );
    }
  }

  // Back of the net, fanned from the middle of the cross-section.
  const centre = vec3(0, GOAL_BACK_Y, GOAL_HEIGHT / 2);
  for (let i = 0; i < n; i++) {
    const p = profile[i];
    const q = profile[(i + 1) % n];
    emit(centre, vec3(p.x, GOAL_BACK_Y, p.z), vec3(q.x, GOAL_BACK_Y, q.z));
  }

  // The four mouth corners. The shell has a rectangular hole cut in it, but the
  // mouth is a ROUNDED rectangle, so a quarter-square-minus-quarter-disc of wall
  // survives at each corner. Arc directions are chosen per corner so that the
  // fan winds toward the field.
  const corners: readonly (readonly [number, number, number, number, number, number])[] = [
    [GOAL_HALF_WIDTH, 0, GOAL_CORNER_X, GOAL_CORNER_Z_LO, 0, -HALF_PI],
    [GOAL_HALF_WIDTH, GOAL_HEIGHT, GOAL_CORNER_X, GOAL_CORNER_Z_HI, HALF_PI, 0],
    [-GOAL_HALF_WIDTH, 0, -GOAL_CORNER_X, GOAL_CORNER_Z_LO, Math.PI + HALF_PI, Math.PI],
    [-GOAL_HALF_WIDTH, GOAL_HEIGHT, -GOAL_CORNER_X, GOAL_CORNER_Z_HI, Math.PI, HALF_PI],
  ];
  for (const [vx, vz, cx, cz, a0, a1] of corners) {
    const v = vec3(vx, Y_WALL, vz);
    for (let j = 0; j < GOAL_ARC_STEPS; j++) {
      const t0 = a0 + ((a1 - a0) * j) / GOAL_ARC_STEPS;
      const t1 = a0 + ((a1 - a0) * (j + 1)) / GOAL_ARC_STEPS;
      emit(
        v,
        vec3(cx + GOAL_FILLET * Math.cos(t0), Y_WALL, cz + GOAL_FILLET * Math.sin(t0)),
        vec3(cx + GOAL_FILLET * Math.cos(t1), Y_WALL, cz + GOAL_FILLET * Math.sin(t1))
      );
    }
  }
};

// ---------------------------------------------------------------------------
// Whole mesh
// ---------------------------------------------------------------------------

/** Build the soccar collision mesh: 9 floats per triangle. */
export const buildSoccarMesh = (): Float64Array => {
  const mesh = new MeshBuilder();
  const ring = buildRing();
  const levels = buildLevels();
  const n = ring.length;

  // Shell.
  for (let k = 0; k + 1 < levels.length; k++) {
    const z0 = levels[k];
    const z1 = levels[k + 1];
    const inGoalBand = z1 <= GOAL_HEIGHT + 1e-9;
    for (let i = 0; i < n; i++) {
      const p = ring[i];
      const q = ring[(i + 1) % n];
      if (inGoalBand && p.goal && q.goal) continue;
      // Wound so the normal points into the field: see the quad note above.
      mesh.quad(ringPos(p, z0), ringPos(p, z1), ringPos(q, z1), ringPos(q, z0));
    }
  }

  // Floor and ceiling caps. Both outlines are star-shaped about the origin, so
  // a radially subdivided fan cannot self-intersect; the subdivision exists
  // purely to keep triangle AABBs small enough for the BVH to be worth having.
  const floorRing = ring.map((p) => ringPos(p, 0));
  const ceilRing = ring.map((p) => ringPos(p, Z_CEIL));
  const origin = vec3(0, 0, 0);
  const ceilOrigin = vec3(0, 0, Z_CEIL);
  for (let m = 0; m < FLOOR_RADIAL_STEPS; m++) {
    const s0 = m / FLOOR_RADIAL_STEPS;
    const s1 = (m + 1) / FLOOR_RADIAL_STEPS;
    for (let i = 0; i < n; i++) {
      const a = floorRing[i];
      const b = floorRing[(i + 1) % n];
      const a0 = vec3(a.x * s0, a.y * s0, 0);
      const b0 = vec3(b.x * s0, b.y * s0, 0);
      const a1 = vec3(a.x * s1, a.y * s1, 0);
      const b1 = vec3(b.x * s1, b.y * s1, 0);
      if (m === 0) mesh.tri(origin, a1, b1);
      else mesh.quad(a0, a1, b1, b0);
    }
  }
  for (let m = 0; m < CEIL_RADIAL_STEPS; m++) {
    const s0 = m / CEIL_RADIAL_STEPS;
    const s1 = (m + 1) / CEIL_RADIAL_STEPS;
    for (let i = 0; i < n; i++) {
      const a = ceilRing[i];
      const b = ceilRing[(i + 1) % n];
      const a0 = vec3(a.x * s0, a.y * s0, Z_CEIL);
      const b0 = vec3(b.x * s0, b.y * s0, Z_CEIL);
      const a1 = vec3(a.x * s1, a.y * s1, Z_CEIL);
      const b1 = vec3(b.x * s1, b.y * s1, Z_CEIL);
      if (m === 0) mesh.tri(ceilOrigin, b1, a1);
      else mesh.quad(a0, b0, b1, a1);
    }
  }

  buildGoal(mesh, 1);
  buildGoal(mesh, -1);

  return mesh.build();
};

// ---------------------------------------------------------------------------
// ArenaGeometry over the mesh
// ---------------------------------------------------------------------------

/**
 * Two contacts count as the same one when their contact points coincide or
 * their normals are parallel. Both cases are pure tessellation artefacts: a
 * flat floor is many coplanar triangles, and a sphere nearest a shared edge is
 * equidistant from both facets that own it. Genuinely different surfaces —
 * every seam the bounce model is judged on — survive.
 */
const SAME_POINT_SQ = 1e-12;
const PARALLEL_DOT = 1 - 1e-9;

/** Floats per gathered sphere candidate: point, normal, depth, alignment. */
const CANDIDATE_STRIDE = 8;

export interface MeshArenaOptions {
  /**
   * Added to every sphere radius passed to `collideSphere`. Defaults to
   * ARENA_COLLISION_MARGIN, which is what puts the ball at BALL_REST_Z.
   */
  margin?: number;
}

export class MeshArena implements ArenaGeometry {
  readonly bvh: BVH;
  readonly margin: number;

  /**
   * Unit face normal of every triangle, 3 floats each, computed once at build
   * time. The mesh is static, so recomputing a normal per query is pure waste —
   * and it was not a small waste: a cross product, a `hypot` and three divides
   * ran for EVERY candidate triangle of every sphere query, which the profiler
   * put at 6 % of the whole simulation.
   */
  private readonly normals: Float64Array;

  private readonly scratch: ClosestPoint = closestPoint();
  private readonly obbHit: OBBTriangleHit = obbTriangleHit();
  /** Growable per-query buffer for `collideSphere`; see `gatherSphere`. */
  private candidates = new Float64Array(0);
  private candidateCount = 0;

  constructor(triangles: Float64Array, options: MeshArenaOptions = {}) {
    this.bvh = new BVH(triangles);
    this.margin = options.margin ?? ARENA_COLLISION_MARGIN;

    const n = this.bvh.triangleCount;
    const tris = this.bvh.triangles;
    this.normals = new Float64Array(n * 3);
    for (let i = 0; i < n; i++) {
      const o = i * 9;
      const e1x = tris[o + 3] - tris[o], e1y = tris[o + 4] - tris[o + 1], e1z = tris[o + 5] - tris[o + 2];
      const e3x = tris[o] - tris[o + 6], e3y = tris[o + 1] - tris[o + 7], e3z = tris[o + 2] - tris[o + 8];
      const nx = e3y * e1z - e3z * e1y;
      const ny = e3z * e1x - e3x * e1z;
      const nz = e3x * e1y - e3y * e1x;
      const len = Math.hypot(nx, ny, nz);
      if (len !== 0) {
        this.normals[i * 3] = nx / len;
        this.normals[i * 3 + 1] = ny / len;
        this.normals[i * 3 + 2] = nz / len;
      }
    }
  }

  get triangles(): Float64Array {
    return this.bvh.triangles;
  }

  get triangleCount(): number {
    return this.bvh.triangleCount;
  }

  /** Face normal of triangle `i`, as stored (pointing into the field). */
  normalOf(i: number): Vec3 {
    const o = i * 3;
    return vec3(this.normals[o], this.normals[o + 1], this.normals[o + 2]);
  }

  /**
   * Ray parameters for `rayIntersector`, on the instance for the same reason
   * `gatherSphere`'s are: the callback is created once at construction instead
   * of once per query. The suspension fires four of these per car per tick and
   * a fresh closure each time both allocates and keeps the BVH's call site
   * polymorphic.
   */
  private rayOX = 0;
  private rayOY = 0;
  private rayOZ = 0;
  private rayDX = 0;
  private rayDY = 0;
  private rayDZ = 0;

  private readonly rayIntersector = (tri: number, best: number): number =>
    rayTriangleFlat(
      this.rayOX, this.rayOY, this.rayOZ,
      this.rayDX, this.rayDY, this.rayDZ,
      this.bvh.triangles, tri * 9, best, false
    );

  raycast(origin: Vec3, direction: Vec3, maxDistance: number, out?: RayHit): RayHit | null {
    const len = Math.hypot(direction.x, direction.y, direction.z);
    if (len === 0 || !(maxDistance > 0)) return null;
    const dx = direction.x / len;
    const dy = direction.y / len;
    const dz = direction.z / len;
    this.rayOX = origin.x;
    this.rayOY = origin.y;
    this.rayOZ = origin.z;
    this.rayDX = dx;
    this.rayDY = dy;
    this.rayDZ = dz;
    const hit = this.bvh.raycastNearest(
      origin.x, origin.y, origin.z, dx, dy, dz, maxDistance, this.rayIntersector
    );
    if (!hit) return null;

    const no = hit.triangle * 3;
    if (out !== undefined) {
      out.distance = hit.t;
      out.point.x = origin.x + dx * hit.t;
      out.point.y = origin.y + dy * hit.t;
      out.point.z = origin.z + dz * hit.t;
      out.normal.x = this.normals[no];
      out.normal.y = this.normals[no + 1];
      out.normal.z = this.normals[no + 2];
      return out;
    }
    return {
      distance: hit.t,
      point: vec3(origin.x + dx * hit.t, origin.y + dy * hit.t, origin.z + dz * hit.t),
      normal: vec3(this.normals[no], this.normals[no + 1], this.normals[no + 2]),
    };
  }

  /**
   * Candidate collector for `collideSphere`, held as a field rather than
   * written inline so that the closure is created once instead of once per
   * query. The sphere parameters travel on `this`; the module is
   * single-threaded and the query is not reentrant, which the BVH's shared
   * traversal stack already required.
   */
  private queryCX = 0;
  private queryCY = 0;
  private queryCZ = 0;
  private queryR = 0;
  private readonly order: number[] = [];

  private readonly gatherSphere = (tri: number): void => {
    const tris = this.bvh.triangles;
    const o = tri * 9;
    const r = this.queryR;
    const nx = this.normals[tri * 3];
    const ny = this.normals[tri * 3 + 1];
    const nz = this.normals[tri * 3 + 2];

    // Plane test before the closest-point test. The distance from a point to a
    // triangle is never less than the distance to its plane, so this is exact
    // and it is nine flops against roughly a hundred. The BVH's AABB filter
    // still hands over plenty of triangles the sphere cannot reach — a ball
    // rolling along a wall gets every nearby floor facet — and this drops them.
    const planeDist =
      (this.queryCX - tris[o]) * nx + (this.queryCY - tris[o + 1]) * ny + (this.queryCZ - tris[o + 2]) * nz;
    if (planeDist > r || planeDist < -r) return;

    // COPLANAR SHORT-CIRCUIT. A triangle can never be deeper than its own
    // plane: `r - |planeDist|` is an upper bound on the depth it could report.
    // If a candidate with a parallel normal already has at least that much
    // depth, this triangle would be merged into it by the dedupe below, so the
    // closest-point test is pure waste.
    //
    // It is not a micro-optimization. The floor cap is a fan whose innermost
    // ring meets at a single vertex at the origin — which is exactly where the
    // ball spawns and where it returns after every goal — so a resting ball
    // sits within reach of roughly 380 coplanar facets and used to run the full
    // closest-point test on every one of them. This collapses that to one.
    const c0 = this.candidates;
    const maxDepth = r - Math.abs(planeDist);
    for (let i = 0; i < this.candidateCount; i++) {
      const q = i * CANDIDATE_STRIDE;
      if (
        c0[q + 3] * nx + c0[q + 4] * ny + c0[q + 5] * nz > PARALLEL_DOT &&
        c0[q + 6] >= maxDepth
      ) {
        return;
      }
    }

    const out = this.scratch;
    closestPointOnTriangleFlat(this.queryCX, this.queryCY, this.queryCZ, tris, o, out);
    if (out.d2 > r * r) return;

    const i = this.candidateCount;
    if ((i + 1) * CANDIDATE_STRIDE > this.candidates.length) {
      const grown = new Float64Array(Math.max(16, (i + 1) * 2) * CANDIDATE_STRIDE);
      grown.set(this.candidates);
      this.candidates = grown;
    }
    const c = this.candidates;
    const s = i * CANDIDATE_STRIDE;
    const dist = Math.sqrt(out.d2);
    c[s] = out.x;
    c[s + 1] = out.y;
    c[s + 2] = out.z;
    c[s + 3] = nx;
    c[s + 4] = ny;
    c[s + 5] = nz;
    c[s + 6] = r - dist;
    // Alignment of the facet normal with the direction from the contact point
    // to the sphere centre. `dist` IS that direction's length, already computed
    // for the depth, so the dedupe's tie-break costs nothing extra here — it
    // used to run a fresh `hypot` inside a sort comparator, i.e. O(n log n)
    // times per query, and the profiler charged it 6 % of the simulation.
    c[s + 7] =
      dist === 0
        ? 0
        : (nx * (this.queryCX - out.x) + ny * (this.queryCY - out.y) + nz * (this.queryCZ - out.z)) /
          dist;
    this.candidateCount = i + 1;
  };

  collideSphere(center: Vec3, radius: number): Contact[] {
    const effective = radius + this.margin;
    this.queryCX = center.x;
    this.queryCY = center.y;
    this.queryCZ = center.z;
    this.queryR = effective;
    this.candidateCount = 0;
    this.bvh.overlapSphere(center.x, center.y, center.z, effective, this.gatherSphere);

    const n = this.candidateCount;
    if (n === 0) return [];
    const c = this.candidates;
    if (n === 1) {
      return [{ point: vec3(c[0], c[1], c[2]), normal: vec3(c[3], c[4], c[5]), depth: c[6] }];
    }

    // Deepest first, and on a tie prefer the facet whose normal best matches the
    // direction from the contact point to the sphere centre — that is the true
    // surface normal where a sphere sits on a shared edge. Indices are sorted
    // rather than objects, so nothing is built for a contact that gets merged.
    // Insertion sort, not `Array.prototype.sort`: `n` is 2..5 in every real
    // query and a comparator call costs more than the whole sort at that size.
    // It is stable, which the merge below depends on.
    const order = this.order;
    order.length = n;
    for (let i = 0; i < n; i++) order[i] = i;
    for (let i = 1; i < n; i++) {
      const key = order[i];
      const kd = c[key * CANDIDATE_STRIDE + 6];
      const ka = c[key * CANDIDATE_STRIDE + 7];
      let j = i - 1;
      while (j >= 0) {
        const other = order[j];
        const od = c[other * CANDIDATE_STRIDE + 6];
        const oa = c[other * CANDIDATE_STRIDE + 7];
        const d = kd - od;
        const after = Math.abs(d) > 1e-9 ? d < 0 : ka <= oa;
        if (after) break;
        order[j + 1] = other;
        j--;
      }
      order[j + 1] = key;
    }

    // Two contacts count as the same one when their points coincide or their
    // normals are parallel. Both cases are pure tessellation artefacts: a flat
    // floor is many coplanar triangles, and a sphere nearest a shared edge is
    // equidistant from both facets that own it. Genuinely different surfaces —
    // every seam the bounce model is judged on — survive.
    const kept: Contact[] = [];
    for (let k = 0; k < n; k++) {
      const o = order[k] * CANDIDATE_STRIDE;
      let duplicate = false;
      for (const prev of kept) {
        const pn = prev.normal;
        if (pn.x * c[o + 3] + pn.y * c[o + 4] + pn.z * c[o + 5] > PARALLEL_DOT) {
          duplicate = true;
          break;
        }
        const dx = c[o] - prev.point.x;
        const dy = c[o + 1] - prev.point.y;
        const dz = c[o + 2] - prev.point.z;
        if (dx * dx + dy * dy + dz * dz < SAME_POINT_SQ) {
          duplicate = true;
          break;
        }
      }
      if (!duplicate) {
        kept.push({
          point: vec3(c[o], c[o + 1], c[o + 2]),
          normal: vec3(c[o + 3], c[o + 4], c[o + 5]),
          depth: c[o + 6],
        });
      }
    }
    return kept;
  }

  /**
   * Car body against the mesh. See `ArenaGeometry.collideOBB`.
   *
   * No collision margin is applied here, unlike `collideSphere`: the ball's
   * 1.9 uu margin exists to reproduce BALL_REST_Z, and the car's resting height
   * comes from the suspension springs, not from its box. Inflating the box would
   * make the body contact the floor 1.9 uu before it really does and would show
   * up as the car refusing to bottom out.
   *
   * Merging is by NORMAL DIRECTION only, keeping the deepest. Two triangles of
   * the same flat panel give the same normal and must count once; the floor and
   * the wall at a seam give different normals and must both survive, because
   * resolving only the deeper of the two is how a car ends up wedged.
   */
  collideOBB(box: OBB, out: Contact[]): number {
    const limit = out.length;
    if (limit === 0) return 0;

    const m = box.orientation;
    const h = box.halfExtents;
    // World AABB of the oriented box: the extent along each world axis is the
    // half-extents dotted with the absolute value of that ROW of the matrix.
    const ex = Math.abs(m[0]) * h.x + Math.abs(m[1]) * h.y + Math.abs(m[2]) * h.z;
    const ey = Math.abs(m[3]) * h.x + Math.abs(m[4]) * h.y + Math.abs(m[5]) * h.z;
    const ez = Math.abs(m[6]) * h.x + Math.abs(m[7]) * h.y + Math.abs(m[8]) * h.z;

    const c = box.center;
    const tris = this.bvh.triangles;
    const hit = this.obbHit;
    let count = 0;

    this.bvh.overlapAABB(
      c.x - ex, c.y - ey, c.z - ez,
      c.x + ex, c.y + ey, c.z + ez,
      (tri) => {
        if (
          !obbTriangleContactFlat(
            c.x, c.y, c.z, m, h.x, h.y, h.z, tris, tri * 9, this.normals, tri * 3, hit
          )
        ) {
          return;
        }
        if (!(hit.depth > 0)) return;

        for (let i = 0; i < count; i++) {
          const kept = out[i];
          const n = kept.normal;
          if (n.x * hit.nx + n.y * hit.ny + n.z * hit.nz > PARALLEL_DOT) {
            if (hit.depth > kept.depth) writeContact(kept, hit);
            return;
          }
        }
        // Buffer full: replace the shallowest contact, so the deepest `limit`
        // survive whatever order the traversal happened to visit them in.
        let slot = count;
        if (count === limit) {
          slot = 0;
          for (let i = 1; i < count; i++) if (out[i].depth < out[slot].depth) slot = i;
          if (out[slot].depth >= hit.depth) return;
        } else {
          count++;
        }
        writeContact(out[slot], hit);
      }
    );

    return count;
  }

  nearestSurface(point: Vec3, maxDistance: number): RayHit | null {
    const tris = this.bvh.triangles;
    const out = this.scratch;
    let bestD2 = maxDistance * maxDistance;
    let bestTri = -1;
    let bx = 0, by = 0, bz = 0;
    this.bvh.overlapSphere(point.x, point.y, point.z, maxDistance, (tri) => {
      closestPointOnTriangleFlat(point.x, point.y, point.z, tris, tri * 9, out);
      if (out.d2 < bestD2) {
        bestD2 = out.d2;
        bestTri = tri;
        bx = out.x;
        by = out.y;
        bz = out.z;
      }
    });
    if (bestTri < 0) return null;
    return { distance: Math.sqrt(bestD2), point: vec3(bx, by, bz), normal: this.normalOf(bestTri) };
  }

  /**
   * The playable volume only: the goal interiors are outside it, which is what
   * "ignoring goals" in the ArenaGeometry contract means. A point on the mouth
   * plane still counts as inside, so a ball crossing the goal line does not
   * flicker.
   */
  contains(point: Vec3): boolean {
    return (
      point.z >= ARENA.floor &&
      point.z <= Z_CEIL &&
      Math.abs(point.x) <= X_WALL &&
      Math.abs(point.y) <= Y_WALL &&
      Math.abs(point.x) + Math.abs(point.y) <= CORNER_C
    );
  }
}

const writeContact = (into: Contact, hit: OBBTriangleHit): void => {
  into.point.x = hit.px;
  into.point.y = hit.py;
  into.point.z = hit.pz;
  into.normal.x = hit.nx;
  into.normal.y = hit.ny;
  into.normal.z = hit.nz;
  into.depth = hit.depth;
};

// ---------------------------------------------------------------------------
// Singletons
// ---------------------------------------------------------------------------

let cachedMesh: Float64Array | null = null;
let cachedArena: MeshArena | null = null;

/**
 * The raw soccar collision triangles, 9 floats each. Memoized; the renderer and
 * the physics share one copy, and nothing mutates it.
 */
export const soccarTriangles = (): Float64Array => {
  if (!cachedMesh) cachedMesh = buildSoccarMesh();
  return cachedMesh;
};

/** The soccar arena. Memoized: building the mesh and the BVH is not free. */
export const soccarArena = (): MeshArena => {
  if (!cachedArena) cachedArena = new MeshArena(soccarTriangles());
  return cachedArena;
};
